import { Button } from '@HoudiniUI/Button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@HoudiniUI/Select';
import {
	buildRegEdSubmissionMetadata,
	IRegEdSubmissionMetadata,
	isRegEdAdvisorSelectableFormat,
	useRegEdMetadataSchemaQuery,
	useRegEdSupportedFormatsQuery,
} from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { ChevronDown } from '../../../svgs/icons/ChevronDown';
import { RegEdMetadataForm } from '../../../compliance/RegEdMetadataForm';
import { CampaignType } from '../../../../../models/AdminModels';
import {
	applyEntryPointAnswers,
	findMissingRequiredFieldIds,
	stripHiddenFieldAnswers,
} from '../../../compliance/RegEdMetadataForm/regEdMetadataFormAdapter';
import { styleSheet } from './styles';

interface IProps {
	impersonationContext?: Api.IImpersonationContext;
	/** Answers the advertising-material question from where the advisor started. */
	campaignType?: CampaignType;
	/** The platforms this post actually targets, which answers the platform question. */
	socialProviders?: readonly Api.SocialMediaType[];
	/** For hosts that share their column with other content — collapses the questionnaire behind a button
	 * and lets the advisor size it. Off where the prompt already owns its panel. */
	collapsible?: boolean;
	disabled?: boolean;
	isLoading?: boolean;
	/** Seed the format/answers when re-submitting a revision, so the advisor edits the prior filing. */
	initialFormat?: string;
	initialAnswers?: Record<string, unknown>;
	/** Fires on every edit so the caller can hold a draft that survives back-navigation (re-seed via the initial props). */
	onFormStateChange?(metadata: IRegEdSubmissionMetadata): void;
	onScheduleRegEdCompliance(e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionMetadata): void;
	styles?: StyleDeclarationValue[];
}

const MIN_FORM_HEIGHT_PX = 220;
const FORM_VIEWPORT_RESERVE_PX = 220;

/** Hosts opt in by tagging their container `data-reged-host` and sizing it from this. */
export const REGED_HOST_HEIGHT_VAR = '--reged-host-height';

/** Keeps a drag from shrinking the questionnaire to nothing or growing it past its host column. */
export const clampFormHeight = (desiredPx: number, viewportHeight = window.innerHeight): number =>
	Math.max(MIN_FORM_HEIGHT_PX, Math.min(viewportHeight - FORM_VIEWPORT_RESERVE_PX, desiredPx));

/**
 * Hands the metadata payload back to the caller rather than submitting — the submissions POST can
 * only happen once the parent (and its server-built approval request) exists.
 */
export const RegEdComplianceApprovalPrompt: React.FC<IProps> = ({
	impersonationContext,
	campaignType,
	socialProviders,
	collapsible,
	disabled,
	isLoading,
	initialFormat,
	initialAnswers,
	onFormStateChange,
	onScheduleRegEdCompliance,
	styles = [],
}) => {
	const [format, setFormat] = React.useState(initialFormat ?? '');
	const [answers, setAnswers] = React.useState<Record<string, unknown>>(initialAnswers ?? {});
	// State set earlier in the same commit is not visible to a later effect, and the seed and the
	// preselect can land together once the schema is cached — so applied answers ride a ref too.
	const answersRef = React.useRef(answers);
	const applyAnswers = (next: Record<string, unknown>) => {
		answersRef.current = next;
		setAnswers(next);
	};
	const [missingFieldIds, setMissingFieldIds] = React.useState<ReadonlySet<string>>(new Set());
	const [expanded, setExpanded] = React.useState(!collapsible);
	const containerRef = React.useRef<HTMLDivElement>(null);

	// The parent may resolve format and answers on different renders; latch each prop separately so
	// a late-arriving answers bag still seeds, and edits are never clobbered.
	const seededFormat = React.useRef(false);
	const seededAnswers = React.useRef(false);
	React.useEffect(() => {
		if (!seededFormat.current && initialFormat !== undefined) {
			setFormat(initialFormat);
			seededFormat.current = true;
		}
		if (!seededAnswers.current && initialAnswers !== undefined) {
			applyAnswers(initialAnswers);
			seededAnswers.current = true;
		}
	}, [initialFormat, initialAnswers]);

	const { data: supportedFormats, isLoading: formatsLoading } = useRegEdSupportedFormatsQuery({ impersonationContext });
	// Firm-level vendor content is filed by a Levitate admin, so the advisor never picks that format.
	const formats = (supportedFormats ?? []).filter(isRegEdAdvisorSelectableFormat);
	const onlyFormat = formats.length === 1 ? formats[0] : undefined;

	// One option isn't a choice. Picking it keeps the advisor from having to open a single-item menu
	// before the questionnaire will load — and makes "choose a format" unreachable rather than a step.
	// A seeded revision format is already set by then, so this never overrides one.
	React.useEffect(() => {
		if (onlyFormat && !format) {
			setFormat(onlyFormat);
			// Tell the caller too — a draft it holds would otherwise record no format at all until the
			// advisor happened to edit an answer. The `!format` guard keeps this to one firing.
			onFormStateChange?.(buildRegEdSubmissionMetadata(onlyFormat, answers));
		}
	}, [onlyFormat, format, answers, onFormStateChange]);

	const { data: schema, isLoading: schemaLoading } = useRegEdMetadataSchemaQuery(format || null, {
		impersonationContext,
	});

	// Collapsed, the panel is just its button — but a schema that never loaded needs to say so somewhere.
	const showDetail = !collapsible || expanded || !schema;

	// Preselecting fills blanks, so it has to happen once and then stop: an answer the advisor clears is blank
	// again, and a fill that re-ran would put it straight back and make the question impossible to change.
	// Keyed on what the preselection is derived from, so a late-arriving entry point still gets one pass.
	const entryPointKey = `${format}|${campaignType ?? ''}|${(socialProviders ?? []).join(',')}`;
	const appliedEntryPointKey = React.useRef<string | null>(null);
	React.useEffect(() => {
		if (!schema || appliedEntryPointKey.current === entryPointKey) {
			return;
		}
		// Latched even when there is nothing to fill, so an unmappable entry point doesn't retry every render.
		appliedEntryPointKey.current = entryPointKey;
		const entryPointAnswers = applyEntryPointAnswers(schema, answersRef.current, campaignType, socialProviders);
		if (!entryPointAnswers) {
			return;
		}
		applyAnswers(entryPointAnswers);
		onFormStateChange?.(buildRegEdSubmissionMetadata(format, entryPointAnswers));
	}, [schema, entryPointKey, campaignType, socialProviders, format, onFormStateChange]);

	const handleFormatChange = (next: string) => {
		setFormat(next);
		applyAnswers({});
		// Field ids can repeat across schemas, so marks earned against the old form would otherwise
		// show the new one as invalid before it has been touched.
		setMissingFieldIds(new Set());
		onFormStateChange?.(buildRegEdSubmissionMetadata(next, {}));
	};

	const handleAnswersChange = (next: Record<string, unknown>) => {
		applyAnswers(next);
		// Clear as they're filled in, so the form stops flagging what the advisor has already fixed.
		setMissingFieldIds(previous =>
			previous.size ? new Set(findMissingRequiredFieldIds(schema, next).filter(id => previous.has(id))) : previous
		);
		onFormStateChange?.(buildRegEdSubmissionMetadata(format, next));
	};

	// The height that matters belongs to the host's container, which sits above this component, so it rides a
	// custom property rather than a prop — the host keeps its own default until the advisor drags.
	const startResize = (e: React.PointerEvent<HTMLDivElement>) => {
		const startY = e.clientY;
		const startHeight = containerRef.current?.closest('[data-reged-host]')?.getBoundingClientRect().height ?? 0;
		e.currentTarget.setPointerCapture(e.pointerId);
		// The grip is the panel's top edge and the panel is anchored below it, so dragging down shrinks it.
		const onMove = (move: PointerEvent) =>
			document.documentElement.style.setProperty(
				REGED_HOST_HEIGHT_VAR,
				`${clampFormHeight(startHeight - (move.clientY - startY))}px`
			);
		const onUp = () => {
			window.removeEventListener('pointermove', onMove);
			window.removeEventListener('pointerup', onUp);
		};
		window.addEventListener('pointermove', onMove);
		window.addEventListener('pointerup', onUp);
	};

	const handleSubmit = (e: React.MouseEvent<HTMLElement>) => {
		if (!format) {
			toast.error('Please choose a submission format.');
			return;
		}
		const missing = findMissingRequiredFieldIds(schema, answers);
		setMissingFieldIds(new Set(missing));
		if (missing.length > 0) {
			toast.error(`Please complete all required fields (${missing.length} remaining).`);
			// The questionnaire is long enough that a count alone leaves the advisor hunting. Targets the
			// field id rather than [data-invalid], which this render hasn't applied yet.
			containerRef.current
				?.querySelector(`[data-field-id="${CSS.escape(missing[0])}"]`)
				// Optional call: jsdom has no scrollIntoView, and this is a nicety either way.
				?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
			return;
		}
		onScheduleRegEdCompliance(e, buildRegEdSubmissionMetadata(format, stripHiddenFieldAnswers(schema, answers)));
	};

	return (
		<div
			className={css(styleSheet.container, collapsible && styleSheet.containerCollapsible, ...styles)}
			ref={containerRef}
		>
			{collapsible && schema && expanded ? (
				<div className={css(styleSheet.resizeHandle)} data-testid='reged-resize-grip' onPointerDown={startResize}>
					<div className={css(styleSheet.resizeGrip)} />
				</div>
			) : null}

			{collapsible && schema ? (
				<Button
					variant='primary'
					styles={[styleSheet.collapseToggle]}
					aria-expanded={expanded}
					aria-controls='reged-questionnaire'
					onClick={() => setExpanded(previous => !previous)}
				>
					<span>{expanded ? 'Hide compliance form' : 'Fill out compliance form'}</span>
					<ChevronDown
						width={16}
						height={16}
						className={css(styleSheet.collapseChevron, expanded && styleSheet.collapseChevronOpen)}
					/>
				</Button>
			) : null}

			{showDetail ? (
				<>
					<div className={css(styleSheet.title)}>Submit for compliance review</div>
					<div className={css(styleSheet.detail)}>
						{onlyFormat
							? 'Complete the compliance questionnaire.'
							: 'Choose the submission format and complete the compliance questionnaire.'}{' '}
						This is sent to your firm's compliance system for review.
					</div>
				</>
			) : null}

			<div className={css(styleSheet.field)} style={showDetail ? undefined : { display: 'none' }}>
				<span className={css(styleSheet.label)}>Format</span>
				{onlyFormat ? (
					// Shown rather than hidden: this is a regulatory filing, so what it is filed under is
					// worth seeing even when there is nothing to decide. Reads the chosen format, not the
					// sole offered one — a revision keeps the format it was originally filed under, which
					// the firm may no longer offer.
					<span className={css(styleSheet.formatValue)}>{format || onlyFormat}</span>
				) : (
					<Select
						value={format}
						onValueChange={handleFormatChange}
						disabled={disabled || isLoading || formatsLoading || formats.length === 0}
					>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<span>{format || (formatsLoading ? 'Loading formats…' : 'Select a format…')}</span>
						</SelectTrigger>
						<SelectContent>
							{formats.map(f => (
								<SelectItem key={f} value={f}>
									{f}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
				{!formatsLoading && formats.length === 0 ? (
					<span className={css(styleSheet.detail)}>No submission formats are configured for your firm.</span>
				) : null}
			</div>

			{format && schemaLoading ? <div className={css(styleSheet.detail)}>Loading questionnaire…</div> : null}
			{format && !schemaLoading && !schema ? (
				<div className={css(styleSheet.detail)}>No compliance questionnaire is configured for this format.</div>
			) : null}
			{schema && expanded ? (
				<div id='reged-questionnaire' className={css(collapsible && styleSheet.scrollingForm)}>
					<RegEdMetadataForm
						schema={schema}
						answers={answers}
						onChange={handleAnswersChange}
						disabled={disabled || isLoading}
						invalidFieldIds={missingFieldIds}
					/>
				</div>
			) : null}

			{!collapsible || expanded ? (
				<Button disabled={disabled || isLoading || !schema} isLoading={isLoading} onClick={handleSubmit}>
					Schedule for Compliance Review
				</Button>
			) : null}
		</div>
	);
};
